import React, { useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import HeaderWrapper from 'components/Header/HeaderWrapper';
import useOptinMonsterDriftInteraction from 'hooks/useOptinMonsterDriftInteraction';
import { useCheckHeroDark } from 'utils/colorUtility';
import { isProd } from 'utils/urlHelper';
import SweetScrollContext from 'utils/SweetScrollContext';
import useGlobalFooter from 'hooks/useGlobalFooter';
import useGlobalHeader from 'hooks/useGlobalHeader';
import { usePromoBanner, useHeaderHeight } from 'hooks/useHeaderHeight';
import useGlobalPromoBar from 'hooks/useGlobalPromoBar';

import PromoBar from 'components/Header/WebsiteHeader/Banner/PromoBar';
import WebsiteFooter from 'components/Footer';

const WebsiteLayout = ({
  id,
  backgroundColor,
  children,
  footer,
  header,
  hero,
  useProgressbar,
  headerTheme, // SIMPLE_HEADER, TRANS_BG_BLACK_COLOR, TRANS_BG_WHITE_COLOR, WHITE_BG_BLACK_COLOR
  layout,
  SubNav,
  anchorLinkNavigation,
  stickyHeader,
}) => {
  const defaultHeaderHeight = 135;
  const [shouldShowGlobalPromoBar, setShouldShowGlobalPromoBar] = useState(false);
  const promoHeight = usePromoBanner(true);
  const measuredHeaderHeight = useHeaderHeight(defaultHeaderHeight);
  const isHeroDark = useCheckHeroDark(hero);
  const { sweetScroll, scrollToAnchorOnLoad } = useContext(SweetScrollContext);
  const globalHeader = useGlobalHeader();
  const globalFooter = useGlobalFooter();
  const globalPromoBar = useGlobalPromoBar();
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const [headerHeight, setHeaderHeight] = useState(defaultHeaderHeight);

  useEffect(() => {
    if (measuredHeaderHeight !== defaultHeaderHeight) {
      setHeaderHeight(measuredHeaderHeight);
    }
  }, [measuredHeaderHeight]);

  const hasGlobalPromoBar = () => {
    if (isProd && globalPromoBar?.hide) {
      return false;
    }

    if (path && globalPromoBar?.excludePaths?.excludePaths) {
      const excludePaths = globalPromoBar.excludePaths.excludePaths
        .split('\n')
        .map((item) => item.trim());
      return !excludePaths.some((excludePath) => {
        if (excludePath.endsWith('/*')) {
          const prefix = excludePath.slice(0, -2);
          return path.startsWith(prefix);
        }
        return path === excludePath;
      });
    }
    return true;
  };

  useEffect(() => {
    if (globalPromoBar && !header?.contentful_id) {
      const result = hasGlobalPromoBar();

      setShouldShowGlobalPromoBar(result);
    }
  }, [header, path, globalPromoBar]);

  if (!header) {
    header = globalHeader;
  }

  if (!footer) {
    footer = globalFooter;
  }

  useOptinMonsterDriftInteraction();

  useEffect(() => {
    window.logoNavigation = (_path) => {
      const logoContainer = document.querySelector('.header-logo-container');
      const svgs = document.querySelectorAll('.header-logo-container svg');
      const oldLink = document.querySelector('.header-logo-container a');

      if (oldLink) {
        oldLink.remove();
      }

      const logoLink = document.createElement('a');
      logoLink.onclick = () => {
        if (typeof window !== 'undefined') {
          window.location.href = _path;
        }
      };
      logoContainer.appendChild(logoLink);

      svgs.forEach((n) => {
        logoLink.appendChild(n);
      });
    };

    window.onpageshow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
  }, [sweetScroll, scrollToAnchorOnLoad]);

  useEffect(() => {
    sweetScroll();
    scrollToAnchorOnLoad();
  }, [sweetScroll, scrollToAnchorOnLoad]);

  const HEADER_THEME =
    header.textColor === 'White'
      ? 'TRANS_BG_WHITE_COLOR'
      : header.textColor === 'Black'
      ? 'TRANS_BG_BLACK_COLOR'
      : headerTheme ||
        (layout === 'toolbox' || layout === 'marketingPro'
          ? 'WHITE_BG_BLACK_COLOR'
          : !hero || (hero && !isHeroDark)
          ? 'TRANS_BG_BLACK_COLOR'
          : 'TRANS_BG_WHITE_COLOR');

  return (
    <WebsiteLayoutContainer id={id} style={{ backgroundColor }} className="website-layout">
      {header?.contentful_id && (
        <HeaderWrapper
          anchorLinkNavigation={anchorLinkNavigation}
          header={header}
          globalPromoBar={globalPromoBar}
          theme={HEADER_THEME}
          noHero={!hero}
          useProgressbar={useProgressbar}
          SubNav={SubNav}
          contentfulId={header?.contentful_id}
        />
      )}
      {shouldShowGlobalPromoBar && (
        <PromoBar
          theme={HEADER_THEME}
          desktopPromoCopy={globalPromoBar?.desktopCopy}
          mobilePromoCopy={globalPromoBar?.mobileCopy}
          promoDetails={globalPromoBar?.details}
        />
      )}
      <MainContainer
        className="main-container"
        noHero={!hero}
        noHeader={!header?.navigation}
        layout={layout}
        stickyHeader={stickyHeader}
        promoHeight={promoHeight}
        headerHeight={headerHeight}
        hasSubNav={!!SubNav}
      >
        {children}
      </MainContainer>
      {footer?.navigation?.navigation && (
        <WebsiteFooter
          anchorId={footer?.contentful_id}
          navigation={footer?.navigation?.navigation}
        />
      )}
    </WebsiteLayoutContainer>
  );
};

if (typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer || [];
  window.Bizible = window.Bizible || { Push: () => {} };
}

WebsiteLayout.propTypes = {
  children: PropTypes.node.isRequired,
  backgroundColor: PropTypes.string,
};

WebsiteLayout.defaultProps = {
  backgroundColor: 'White',
};

export default WebsiteLayout;

const WebsiteLayoutContainer = styled.div`
  position: relative;
`;

const MainContainer = styled.main`
  position: relative;
  padding-top: 0;
`;
